import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from 'helpers/api'
import { IContextMenuState, IUserObject } from 'types'
import { useAuth } from 'hooks/auth'
import { HOME } from 'routes'
import { mapUsersToTableData } from 'views/admin/helpers/utils'
import Modal from 'components/modal'
import Button from 'components/button'
import Menu from 'components/menu'
import DeleteModal from 'components/delete'
import { Table } from 'components/table'
import Select from 'components/select'
import { Delete, Lock, Pencil, People, Settings, Restore } from 'components/icons'
import {
  INITIAL_CONTEXT_MENU_STATE,
  USER_SUBSCRIPTION_LEVELS,
  REGEX_SPECIAL_CHARACTERS_PASSWORD,
  ALLOWED_SPECIAL_CHARACTERS_PASSWORD,
  REGEX_PASSWORD_MIN_LENGTH,
  MIN_PASSWORD_LENGTH
} from 'app-constants'
import { useApplicationStore } from 'hooks/application'
import { numberWithCommas } from 'components/spreadsheet/helpers/format'
import Skeleton from 'components/skeleton'
import Input from 'components/input'

interface IAdminUserObject extends IUserObject {
  password: string | null
  subscriptionLevel: number
}

const SEARCH_DEBOUNCE_MS = 600

type PerPageOption = '50' | '100' | '200'
const PER_PAGE_OPTIONS: { label: string; value: PerPageOption }[] = [
  { label: '50', value: '50' },
  { label: '100', value: '100' },
  { label: '200', value: '200' }
]

const AdminHome: React.FC = () => {
  const navigate = useNavigate()

  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()

  const [totalUsers, setTotalUsers] = useState<number>()
  const [paidUsers, setPaidUsers] = useState<number>()
  const [users, setUsers] = useState<IUserObject[]>([])
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false)
  const [editUserModal, setEditUserModal] = useState<IAdminUserObject>()

  const [userEmailSearch, setUserEmailSearch] = useState<string>('')
  const [debouncedEmailSearch, setDebouncedEmailSearch] = useState<string>('')
  const [userSubscriptionSearch, setUserSubscriptionSearch] = useState<number>(-1)
  const [page, setPage] = useState<number>(1)
  const [total, setTotal] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [perPageOption, setPerPageOption] = useState<PerPageOption>('50')

  const perPage = Number.parseInt(perPageOption)
  const [deleteTarget, setDeleteTarget] = useState<{ open: boolean; publicId: string; email: string }>({
    open: false,
    publicId: '',
    email: ''
  })
  const [restoreTarget, setRestoreTarget] = useState<{ open: boolean; publicId: string; email: string }>({
    open: false,
    publicId: '',
    email: ''
  })
  const [forceSupportAccessTarget, setForceSupportAccessTarget] = useState<IUserObject>()
  const [forceSupportAccessReason, setForceSupportAccessReason] = useState<string>('')
  const [forcingSupportAccess, setForcingSupportAccess] = useState<boolean>(false)
  const [orgTeams, setOrgTeams] = useState<
    { publicId: string; name: string; seatLimit: number; seatsUsed: number; isDefault: boolean }[]
  >([])
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null)
  const { startMasquerade } = useAuth()

  const [optionsMenu, setOptionsMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [optionsTarget, setOptionsTarget] = useState<IUserObject | null>(null)

  const handleShowOptions = (user: IUserObject, event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    setOptionsTarget(user)
    setOptionsMenu({
      open: true,
      top: `${rect.bottom + 4}px`,
      left: `${rect.right - 200}px`,
      right: 'auto',
      bottom: 'auto'
    })
  }

  useEffect(() => {
    document.title = 'Morta | Admin Panel'
  }, [])

  useEffect(() => {
    api
      .getTotalUsers()
      .then((response) => {
        setTotalUsers(response.data.totalUsers)
        setPaidUsers(response.data.paidUsers)
      })
      .catch((e) => {
        displayErrorMessage(e)
      })
  }, [])

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedEmailSearch(userEmailSearch)
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [userEmailSearch])

  useEffect(() => {
    setLoadingUsers(true)
    api
      .getAllUsers(
        debouncedEmailSearch,
        userSubscriptionSearch === -1 ? undefined : userSubscriptionSearch,
        page,
        perPage
      )
      .then((response) => {
        setUsers(response.data)
        setTotal(response.metadata?.total ?? response.data.length)
        const newTotalPages = response.metadata?.totalPages ?? 1
        setTotalPages(newTotalPages)
        if (page > newTotalPages) setPage(Math.max(1, newTotalPages))
      })
      .catch((e) => {
        displayErrorMessage(e)
      })
      .finally(() => {
        setLoadingUsers(false)
      })
  }, [page, perPage, debouncedEmailSearch, userSubscriptionSearch])

  const createLoginHandler = async (user: IUserObject) => {
    try {
      const response = await api.getMasqueradeCredentials(user)
      startMasquerade(response.data.token)
      navigate(HOME)
    } catch (e) {
      displayErrorMessage(e)
    }
  }

  const verifyUserEmail = async (user: IUserObject) => {
    let response
    try {
      response = await api.verifyUserEmail(user.publicId)
    } catch (e) {
      displayErrorMessage(e)
      return
    }
    if (response.data && users) {
      const newUsers = [...users].map((userObject: IUserObject) => {
        if (userObject.publicId === user.publicId) {
          return { ...userObject, isEmailVerified: true }
        } else {
          return userObject
        }
      })

      setUsers(newUsers)
    }
  }

  const handleDeleteUser = async () => {
    try {
      const response = await api.deleteUser(deleteTarget.publicId)
      if (response.data) {
        const deletedUser = response.data
        setUsers((prev) =>
          prev.map((userObject) =>
            userObject.publicId === deleteTarget.publicId ? { ...userObject, ...deletedUser } : userObject
          )
        )
        setSnackbarMessage({ status: 'success', message: 'User deleted successfully' })
      }
    } catch (e) {
      displayErrorMessage(e)
    }
  }

  const handleRestoreUser = async () => {
    try {
      const response = await api.restoreUser(restoreTarget.publicId)
      if (response.data) {
        const restoredUser = response.data
        setUsers((prev) =>
          prev.map((userObject) =>
            userObject.publicId === restoreTarget.publicId ? { ...userObject, ...restoredUser } : userObject
          )
        )
        setSnackbarMessage({ status: 'success', message: 'User restored successfully' })
      }
      setRestoreTarget((prev) => ({ ...prev, open: false }))
    } catch (e) {
      displayErrorMessage(e)
    }
  }

  const remove2fa = async (user: IUserObject) => {
    let response
    try {
      response = await api.adminRemove2fa(user.publicId)
    } catch (e) {
      displayErrorMessage(e)
      return
    }
    if (response.data && users) {
      const newUsers = [...users].map((userObject: IUserObject) => {
        if (userObject.publicId === user.publicId) {
          return { ...userObject, is2FaEnabled: false }
        } else {
          return userObject
        }
      })

      setUsers(newUsers)
    }
  }

  const closeForceSupportAccessModal = () => {
    setForceSupportAccessTarget(undefined)
    setForceSupportAccessReason('')
  }

  const handleForceSupportAccess = async () => {
    if (!forceSupportAccessTarget || !forceSupportAccessReason.trim()) return

    const target = forceSupportAccessTarget
    setForcingSupportAccess(true)
    try {
      const response = await api.forceSupportAccess(target.publicId, forceSupportAccessReason.trim())
      setUsers((prev) =>
        prev.map((userObject) =>
          userObject.publicId === target.publicId
            ? {
                ...userObject,
                allowSupportAccess: response.data.allowSupportAccess,
                allowSupportAccessExpiresAt: response.data.allowSupportAccessExpiresAt
              }
            : userObject
        )
      )
      setSnackbarMessage({
        status: 'success',
        message: `Support access forced for ${target.email} for the next 48 hours`
      })
      closeForceSupportAccessModal()
      setForcingSupportAccess(false)
    } catch (e) {
      displayErrorMessage(e)
      setForcingSupportAccess(false)
    }
  }

  const clearRateLimit = async (user: IUserObject) => {
    try {
      await api.adminClearRateLimit(user.publicId)
    } catch (e) {
      displayErrorMessage(e)
      return
    }
    setUsers((prev) =>
      prev.map((userObject) =>
        userObject.publicId === user.publicId ? { ...userObject, isRateLimited: false } : userObject
      )
    )
    setSnackbarMessage({ status: 'success', message: `Rate limit cleared for ${user.email}` })
  }

  const handleEditUser = (user: IUserObject) => {
    setEditUserModal({ ...user, password: null })
    setSelectedTeamId(user.team?.teamPublicId || null)
    setOrgTeams([])

    api
      .adminGetUserTeams(user.publicId)
      .then((response) => setOrgTeams(response.data || []))
      .catch(() => setOrgTeams([]))
  }

  const passwordLengthError = !(
    editUserModal &&
    editUserModal.password &&
    editUserModal.password.match(REGEX_PASSWORD_MIN_LENGTH)
  )
  const passwordLowercaseError = !(
    editUserModal &&
    editUserModal.password &&
    editUserModal.password.match(/^(?=.*[a-z])/)
  )
  const passwordUppercaseError = !(
    editUserModal &&
    editUserModal.password &&
    editUserModal.password.match(/^(?=.*[A-Z])/)
  )
  const passwordNumericError = !(
    editUserModal &&
    editUserModal.password &&
    editUserModal.password.match(/^(?=.*[0-9])/)
  )
  const passwordSpecialError = !(
    editUserModal &&
    editUserModal.password &&
    editUserModal.password.match(REGEX_SPECIAL_CHARACTERS_PASSWORD)
  )

  const passwordError =
    passwordLengthError ||
    passwordLowercaseError ||
    passwordUppercaseError ||
    passwordNumericError ||
    passwordSpecialError

  const handleUserSave = async () => {
    if (
      !editUserModal ||
      !(
        editUserModal.password === undefined ||
        editUserModal.password === null ||
        editUserModal.password === '' ||
        (editUserModal.password && !passwordError)
      )
    ) {
      console.error('There is something wrong with the password.')
      return
    }

    const isSuperAdmin = editUserModal.isSuperAdmin ? true : false
    const teamId = editUserModal.userLevelSubscription === 1 ? selectedTeamId : null

    if (
      editUserModal.userLevelSubscription === 1 &&
      orgTeams.length === 0 &&
      !editUserModal.team &&
      !window.confirm(
        'This user is not part of an organisation and will not be assigned to any team. ' +
          'They will be a "floating" paid user. Are you sure you want to continue?'
      )
    ) {
      return
    }

    api
      .adminUpdateUser(
        editUserModal.publicId,
        editUserModal.name,
        editUserModal.email,
        editUserModal.password,
        editUserModal.userLevelSubscription,
        editUserModal.eligibleForFreeTrial,
        isSuperAdmin,
        teamId
      )
      .then((response) => {
        if (users) {
          const newUsers = users.map((userObject: IUserObject) =>
            userObject.publicId === editUserModal.publicId ? { ...userObject, ...response.data } : userObject
          )
          setUsers(newUsers)
          setSnackbarMessage({
            status: 'success',
            message: 'User updated successfully'
          })
        }
        setEditUserModal(undefined)
      })
      .catch((e) => {
        displayErrorMessage(e)
      })
  }

  const tableData = mapUsersToTableData(
    users,
    createLoginHandler,
    verifyUserEmail,
    handleEditUser,
    remove2fa,
    setSnackbarMessage,
    handleShowOptions
  )

  const copyTableDataToClipboard = () => {
    // Define headers based on the include array
    const headers = [
      'Email',
      'Public Id',
      'Signed Up',
      'Paid',
      'Team',
      'Paid User Seats',
      '2fa Enabled',
      'Email Verified',
      'Support Access',
      'Super Admin',
      'Rate Limited',
      'AI Features Enabled'
    ]

    // Convert raw user data to tab-separated format with Windows line endings
    const tsvContent = [
      headers.join('\t'),
      ...users.map((user) =>
        [
          user.email,
          user.publicId,
          user.createdAt ? new Date(user.createdAt).toLocaleString() : '',
          user.subscriptionLevel === 1 ? (user.isOnFreeTrial ? 'Trial' : 'Yes') : 'No',
          user.team ? `${user.team.orgName} › ${user.team.teamName}` : '',
          user.managedSeatCount != null ? user.managedSeatCount : '',
          user.is2FaEnabled ? 'Yes' : 'No',
          user.isEmailVerified ? 'Yes' : 'No',
          user.allowSupportAccess ? 'Yes' : 'No',
          user.isSuperAdmin ? 'Yes' : 'No',
          user.isRateLimited ? 'Yes' : 'No'
        ].join('\t')
      )
    ].join('\r\n')

    // Copy to clipboard
    navigator.clipboard
      .writeText(tsvContent)
      .then(() => {
        setSnackbarMessage({
          status: 'success',
          message: 'User data copied to clipboard'
        })
      })
      .catch((error) => {
        console.error('Failed to copy to clipboard:', error)
        displayErrorMessage(error)
      })
  }

  return (
    <div className="w-full h-full flex flex-col select-none p-5">
      <div className="flex w-full items-center mb-[15px] gap-[30px]">
        <div className="flex flex-col h-full">
          <div className="italic text-muted text-sm mb-[5px]"># Users On Platform</div>
          {totalUsers !== undefined ? (
            <div className="ml-auto text-xl">{numberWithCommas(totalUsers)}</div>
          ) : (
            <Skeleton className="ml-auto h-[28.695px] w-[50px]" />
          )}
        </div>

        <div className="flex flex-col h-full">
          <div className="italic text-muted text-sm mb-[5px]"># Paid Users On Platform</div>
          {paidUsers !== undefined && totalUsers !== undefined ? (
            <div className="ml-auto text-xl">{numberWithCommas(paidUsers)}</div>
          ) : (
            <Skeleton className="ml-auto h-[28.695px] w-[50px]" />
          )}
        </div>

        <div className="flex flex-col ml-auto w-[200px]">
          <div className="italic text-muted text-sm mb-[5px]">Subscription Filter:</div>
          <Select
            options={[
              { label: 'All', value: '-1' },
              ...USER_SUBSCRIPTION_LEVELS.map((level, levelIndex) => {
                return { label: level, value: levelIndex.toString() }
              })
            ]}
            onOptionClick={(option) => {
              setUserSubscriptionSearch(Number.parseInt(option))
              setPage(1)
            }}
            optionsSelected={[userSubscriptionSearch.toString()]}
          />
        </div>

        <div className="flex flex-col w-[200px]">
          <div className="italic text-muted text-sm mb-[5px]">Email Filter:</div>
          <Input
            type="text"
            placeholder="Search by name or email..."
            onChange={(event) => setUserEmailSearch(event.target.value)}
          />
        </div>

        <div className="flex flex-col ml-5">
          <div className="italic text-muted text-sm mb-[5px]">Export Data</div>
          <Button internalType="secondary" onClick={copyTableDataToClipboard}>
            Copy as CSV
          </Button>
        </div>
      </div>

      <Table
        data={tableData}
        include={[
          { header: 'Email', id: 'email' },
          { header: 'Is Deleted', id: 'isDeleted' },
          { header: 'Public Id', id: 'publicId' },
          { header: 'Signed Up', id: 'createdAt' },
          { header: 'Paid', id: 'paid' },
          { header: '2fa Enabled?', id: 'is2faEnabled' },
          { header: 'Email Verified?', id: 'verified' },
          { header: 'Support Access?', id: 'allowSupportAccess' },
          { header: 'Super Admin', id: 'isSuperAdmin' },
          { header: 'Rate Limited', id: 'isRateLimited' },
          { header: 'Team', id: 'team' },
          { header: 'Paid User Seats', id: 'managedSeatCount' },
          {
            header: (
              <div className="w-full flex items-center justify-center">
                <Settings className="size-4" />
              </div>
            ),
            id: 'options'
          }
        ]}
        defaultSort={'email'}
        defaultSortAscending={false}
        loading={loadingUsers}
      />

      <div className="flex flex-row flex-wrap items-center justify-start gap-2.5 mt-2.5">
        <span className="text-sm text-muted">Rows per page</span>
        <Select
          className="w-[100px] h-full py-[7px]"
          options={PER_PAGE_OPTIONS}
          optionsSelected={[perPageOption]}
          onOptionClick={(value) => {
            setPerPageOption(value as PerPageOption)
            setPage(1)
          }}
        />
        <Button
          internalType="outline"
          disabled={page <= 1 || loadingUsers}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          Prev
        </Button>
        <span className="text-sm text-muted">
          Page {page} of {totalPages}
        </span>
        <Button
          internalType="outline"
          disabled={page >= totalPages || loadingUsers}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </Button>
        <span className="text-sm text-muted">
          ({total === 0 ? 0 : (page - 1) * perPage + 1}-{Math.min(page * perPage, total)} of {total})
        </span>
      </div>

      {editUserModal && (
        <Modal
          id="edit-user-modal"
          open={editUserModal !== undefined}
          setOpen={() => setEditUserModal(undefined)}
          title={`Edit User`}
        >
          <div className="mb-8">
            <div className="font-bold mb-4">User Name</div>
            <Input
              type="text"
              defaultValue={editUserModal.name}
              onChange={(event) => setEditUserModal({ ...editUserModal, name: event.target.value })}
              disabled={editUserModal.isDeleted}
            />
          </div>
          <div className="mb-8">
            <div className="font-bold mb-4">User Email</div>
            <Input
              type="text"
              defaultValue={editUserModal.email}
              onChange={(event) => setEditUserModal({ ...editUserModal, email: event.target.value })}
              disabled={editUserModal.isDeleted}
            />
          </div>
          <div className="mb-8">
            <div className="font-bold mb-4">New User Password</div>
            <Input
              type="password"
              onChange={(event) => setEditUserModal({ ...editUserModal, password: event.target.value })}
              autoComplete="new-password"
              disabled={editUserModal.isDeleted}
            />
            {editUserModal.password && editUserModal.password !== '' && (
              <p className={`${passwordLengthError ? 'text-red' : 'text-green'} mt-2.5`}>
                {`${passwordLengthError ? '⤫' : '✓'} Password must be at least ${MIN_PASSWORD_LENGTH} characters long`}
              </p>
            )}
            {editUserModal.password && editUserModal.password !== '' && (
              <p className={`${passwordLowercaseError ? 'text-red' : 'text-green'} mt-2.5`}>
                {`${passwordLowercaseError ? '⤫' : '✓'} Password must contain at least one lowercase letter`}
              </p>
            )}
            {editUserModal.password && editUserModal.password !== '' && (
              <p className={`${passwordUppercaseError ? 'text-red' : 'text-green'} mt-2.5`}>
                {`${passwordUppercaseError ? '⤫' : '✓'} Password must contain at least one uppercase letter`}
              </p>
            )}
            {editUserModal.password && editUserModal.password !== '' && (
              <p className={`${passwordNumericError ? 'text-red' : 'text-green'} mt-2.5`}>
                {`${passwordNumericError ? '⤫' : '✓'} Password must contain at least one numeric character`}
              </p>
            )}
            {editUserModal.password && editUserModal.password !== '' && (
              <p className={`${passwordSpecialError ? 'text-red' : 'text-green'} mt-2.5`}>
                {`${
                  passwordSpecialError ? '⤫' : '✓'
                } Password must contain at least one special character: ${ALLOWED_SPECIAL_CHARACTERS_PASSWORD}`}
              </p>
            )}
          </div>

          <div className="mb-8">
            <div className="font-bold mb-4">Super Admin</div>
            <Select
              options={[
                {
                  label: 'Yes',
                  value: 'true'
                },
                {
                  label: 'No',
                  value: 'false'
                }
              ]}
              onOptionClick={(option) => {
                setEditUserModal({ ...editUserModal, isSuperAdmin: option === 'true' })
              }}
              optionsSelected={[editUserModal.isSuperAdmin ? 'true' : 'false']}
              disabled={editUserModal.isDeleted}
            />
          </div>

          <div className="mb-8">
            <div className="font-bold mb-4">Paid</div>
            {editUserModal.team ? (
              <p className="text-sm text-muted">
                This user is assigned to team <strong>{editUserModal.team.teamName}</strong>. To change their paid
                status, manage their seat in the teams dashboard.
              </p>
            ) : (
              <>
                <Select
                  options={[
                    {
                      label: 'Yes',
                      value: '1'
                    },
                    {
                      label: 'No',
                      value: '0'
                    }
                  ]}
                  onOptionClick={(option) => {
                    const isPaid = option === '1'
                    setEditUserModal({
                      ...editUserModal,
                      userLevelSubscription: isPaid ? 1 : 0,
                      eligibleForFreeTrial: isPaid ? false : editUserModal.eligibleForFreeTrial
                    })
                    if (!isPaid) setSelectedTeamId(null)
                  }}
                  optionsSelected={[String(editUserModal.userLevelSubscription)]}
                  disabled={editUserModal.isDeleted}
                />
                <p className="text-sm text-muted mt-2.5">Paid users automatically have their free trial ended.</p>
              </>
            )}
          </div>

          {editUserModal.userLevelSubscription === 1 && orgTeams.length > 0 && !editUserModal.team && (
            <div className="mb-8">
              <div className="font-bold mb-4">Assign to Team</div>
              <Select
                options={orgTeams.map((t) => ({
                  label: `${t.name} (${t.seatsUsed}/${t.seatLimit} seats)`,
                  value: t.publicId
                }))}
                onOptionClick={(value) => setSelectedTeamId(value)}
                optionsSelected={selectedTeamId ? [selectedTeamId] : []}
                disabled={editUserModal.isDeleted}
              />
              {!selectedTeamId && <p className="text-sm text-red mt-2.5">A team must be selected before saving.</p>}
            </div>
          )}

          {editUserModal.userLevelSubscription !== 1 && (
            <div className="mb-8">
              <div className="font-bold mb-4">On Free Trial</div>
              <Select
                options={[
                  {
                    label: 'Yes',
                    value: 'true'
                  },
                  {
                    label: 'No',
                    value: 'false'
                  }
                ]}
                onOptionClick={(option) => {
                  setEditUserModal({ ...editUserModal, eligibleForFreeTrial: option === 'true' })
                }}
                optionsSelected={[editUserModal.eligibleForFreeTrial ? 'true' : 'false']}
                disabled={editUserModal.isDeleted}
              />
            </div>
          )}

          <div className="flex items-center justify-end">
            <Button className="ml-2.5" internalType="danger" onClick={() => setEditUserModal(undefined)}>
              Cancel
            </Button>
            {!editUserModal.isDeleted && (
              <Button
                className="ml-2.5"
                internalType="accept"
                onClick={() => handleUserSave()}
                disabled={editUserModal.userLevelSubscription === 1 && orgTeams.length > 0 && !selectedTeamId}
              >
                Update User
              </Button>
            )}
          </div>
        </Modal>
      )}

      <Menu
        id="admin-user-options-menu"
        menuState={optionsMenu}
        setMenuState={setOptionsMenu}
        width={200}
        onClose={() => setOptionsTarget(null)}
      >
        <div className="flex flex-col py-1">
          <div
            className="flex flex-row items-center gap-2.5 px-3 py-2 hover:bg-surface-sunken text-left cursor-pointer"
            onClick={() => {
              if (!optionsTarget) return
              handleEditUser(optionsTarget)
              setOptionsMenu(INITIAL_CONTEXT_MENU_STATE)
              setOptionsTarget(null)
            }}
          >
            <Pencil />
            {optionsTarget?.isDeleted ? 'View' : 'Edit'}
          </div>
          {optionsTarget && optionsTarget.isEmailVerified && !optionsTarget.isDeleted && (
            <div
              className="flex flex-row items-center gap-2.5 px-3 py-2 hover:bg-surface-sunken text-left cursor-pointer"
              onClick={() => {
                if (!optionsTarget) return
                createLoginHandler(optionsTarget)
                setOptionsMenu(INITIAL_CONTEXT_MENU_STATE)
                setOptionsTarget(null)
              }}
            >
              <People />
              Login
            </div>
          )}
          {optionsTarget && !optionsTarget.isDeleted && (
            <div
              data-testid="force-support-access-option"
              className="flex flex-row items-center gap-2.5 px-3 py-2 hover:bg-surface-sunken text-left cursor-pointer"
              onClick={() => {
                if (!optionsTarget) return
                setForceSupportAccessTarget(optionsTarget)
                setForceSupportAccessReason('')
                setOptionsMenu(INITIAL_CONTEXT_MENU_STATE)
                setOptionsTarget(null)
              }}
            >
              <Lock />
              Force Support Access
            </div>
          )}
          {optionsTarget && optionsTarget.isRateLimited && !optionsTarget.isDeleted && (
            <div
              className="flex flex-row items-center gap-2.5 px-3 py-2 hover:bg-surface-sunken text-left cursor-pointer"
              onClick={() => {
                if (!optionsTarget) return
                clearRateLimit(optionsTarget)
                setOptionsMenu(INITIAL_CONTEXT_MENU_STATE)
                setOptionsTarget(null)
              }}
            >
              <Settings />
              Clear Rate Limit
            </div>
          )}
          {optionsTarget && !optionsTarget.isDeleted && (
            <div
              className="flex flex-row items-center gap-2.5 px-3 py-2 hover:bg-surface-sunken text-left text-red cursor-pointer"
              onClick={() => {
                if (!optionsTarget) return
                setDeleteTarget({ open: true, publicId: optionsTarget.publicId, email: optionsTarget.email })
                setOptionsMenu(INITIAL_CONTEXT_MENU_STATE)
                setOptionsTarget(null)
              }}
            >
              <Delete />
              Delete
            </div>
          )}
          {optionsTarget && optionsTarget.isDeleted && (
            <div
              className="flex flex-row items-center gap-2.5 px-3 py-2 hover:bg-surface-sunken text-left cursor-pointer"
              onClick={() => {
                if (!optionsTarget) return
                setRestoreTarget({ open: true, publicId: optionsTarget.publicId, email: optionsTarget.email })
                setOptionsMenu(INITIAL_CONTEXT_MENU_STATE)
                setOptionsTarget(null)
              }}
            >
              <Restore />
              Restore
            </div>
          )}
        </div>
      </Menu>

      <DeleteModal
        id="delete-user-modal"
        open={deleteTarget.open}
        setOpen={(open) => setDeleteTarget((prev) => ({ ...prev, open }))}
        resourceName={deleteTarget.email}
        deleteResource={handleDeleteUser}
        notUndo
        onError={(e) => displayErrorMessage(e)}
      />

      {forceSupportAccessTarget && (
        <Modal
          id="force-support-access-modal"
          open={forceSupportAccessTarget !== undefined}
          setOpen={(open) => {
            if (!open) closeForceSupportAccessModal()
          }}
          title="Force Support Access"
        >
          <div className="mb-8">
            <div className="mb-4">
              Grant support access on <span className="font-bold">{forceSupportAccessTarget.email}</span> for the next
              48 hours without their approval. This bypasses their consent, so the reason below is recorded against your
              account in the platform audit log.
            </div>
            <div className="font-bold mb-2.5">Reason</div>
            <Input
              data-testid="force-support-access-reason"
              type="text"
              maxLength={500}
              placeholder="Why does support access need to be forced?"
              value={forceSupportAccessReason}
              onChange={(event) => setForceSupportAccessReason(event.target.value)}
            />
            {!forceSupportAccessReason.trim() && (
              <p className="text-sm text-red mt-2.5">A reason is required before support access can be forced.</p>
            )}
          </div>
          <div className="flex items-center justify-end">
            <Button className="ml-2.5" internalType="danger" onClick={closeForceSupportAccessModal}>
              Cancel
            </Button>
            <Button
              data-testid="force-support-access-confirm"
              className="ml-2.5"
              internalType="accept"
              disabled={!forceSupportAccessReason.trim() || forcingSupportAccess}
              onClick={() => handleForceSupportAccess()}
            >
              Force Support Access
            </Button>
          </div>
        </Modal>
      )}

      {restoreTarget.open && (
        <Modal
          id="restore-user-modal"
          open={restoreTarget.open}
          setOpen={(open) => setRestoreTarget((prev) => ({ ...prev, open }))}
          title={`Restore User`}
        >
          <div className="mb-8">
            Restore <span className="font-bold">{restoreTarget.email}</span>? This brings back their account record only
            — they will still need to reset their password and be re-invited to regain access.
          </div>
          <div className="flex items-center justify-end">
            <Button
              className="ml-2.5"
              internalType="danger"
              onClick={() => setRestoreTarget((prev) => ({ ...prev, open: false }))}
            >
              Cancel
            </Button>
            <Button className="ml-2.5" internalType="accept" onClick={() => handleRestoreUser()}>
              Restore User
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default AdminHome
